/* ==========================================================================
   Header Web Components
   - StickyHeader: Manages sticky behavior, hide/reveal on scroll
   - HeaderNavIndicator: Sliding underline for nav links
   - MobileDrawer: Mobile menu slide-in drawer
   ========================================================================== */

// ---------- StickyHeader Component ----------
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.navBar = this.querySelector('.header');
    this.headerBounds = {};
    this.isTransparent = this.navBar.classList.contains('header--transparent');

    this.setHeaderHeight();

    // Prevent transition flash on page load
    this.navBar.classList.add('no-transition');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.navBar.classList.remove('no-transition');
      });
    });

    // Update header CSS vars ONLY on external resize/font events.
    // (Previous ResizeObserver fired every frame of hide/reveal transition,
    // pushing 100+ CSS var changes per scroll burst and causing a layout loop.)
    this.onResize = () => this.setHeaderHeight();
    window.addEventListener('resize', this.onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.setHeaderHeight());
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
    this.initNavIndicator();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    document.documentElement.style.setProperty('--header-nav-actual-height', `${this.navBar.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    // Style threshold: when to switch transparent → solid (small, so styles
    // change before the header hides — prevents flash on reveal)
    const styleThreshold = 50;
    // Hide threshold: how far down before the header starts hiding on scroll-down
    const hideThreshold = 150;

    // 1. Style switching — purely position-based, independent of hide/show
    if (this.isTransparent) {
      if (scrollTop > styleThreshold) {
        this.header.classList.add('scrolled-past-header');
      } else {
        this.header.classList.remove('scrolled-past-header');
      }
    }

    // 2. Hide/reveal logic — separate from style switching
    const scrollingDown = scrollTop > this.currentScrollTop;
    const scrollingUp = scrollTop < this.currentScrollTop;

    if (scrollingDown && scrollTop > hideThreshold) {
      if (this.preventHide) return;
      requestAnimationFrame(() => this.hide());
    } else if (scrollingUp && scrollTop > hideThreshold) {
      if (!this.preventReveal) {
        requestAnimationFrame(() => this.reveal());
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(() => this.hide());
      }
    } else if (scrollTop <= hideThreshold) {
      requestAnimationFrame(() => this.reveal());
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.navBar.classList.contains('shopify-header-hidden')) return;
    this.navBar.classList.add('shopify-header-hidden');
  }

  reveal() {
    if (!this.navBar.classList.contains('shopify-header-hidden')) return;
    this.navBar.classList.remove('shopify-header-hidden');
  }

  reset() {
    this.navBar.classList.remove('shopify-header-hidden');
  }

  // ---------- Nav Indicator Logic (Integrated) ----------
  initNavIndicator() {
    // Only run on desktop
    if (window.innerWidth < 990) return;

    const nav = this.querySelector('.header__nav');
    if (!nav) return;

    const menuLinks = nav.querySelectorAll('.header__menu-link');
    const indicator = nav.querySelector('.header__nav-indicator');

    if (!indicator || menuLinks.length === 0) return;

    // Find active link on page load
    const activeLink = nav.querySelector('.header__menu-link--active');

    if (activeLink) {
      this.updateIndicator(indicator, activeLink);
      indicator.classList.add('is-visible');
    }

    // Hover interactions
    menuLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        this.updateIndicator(indicator, link);
        indicator.classList.add('is-visible');
      });
    });

    nav.addEventListener('mouseleave', () => {
      if (activeLink) {
        this.updateIndicator(indicator, activeLink);
      } else {
        indicator.classList.remove('is-visible');
      }
    });

    // Reposition on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth >= 990 && activeLink) {
          this.updateIndicator(indicator, activeLink);
        }
      }, 100);
    });
  }

  updateIndicator(indicator, link) {
    const navRect = link.closest('.header__nav').getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    indicator.style.left = `${linkRect.left - navRect.left}px`;
    indicator.style.width = `${linkRect.width}px`;
  }
}

customElements.define('sticky-header', StickyHeader);


// ---------- MobileDrawer Component ----------
class MobileDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer]');
    this.backdrop = this.querySelector('[data-drawer-backdrop]');
    this.trigger = document.querySelector('[data-drawer-trigger]');
    this.closeBtn = this.querySelector('[data-drawer-close]');
    this.submenuToggles = this.querySelectorAll('[data-submenu-toggle]');

    if (!this.drawer || !this.backdrop || !this.trigger) return;

    // Open drawer
    this.trigger.addEventListener('click', () => this.open());

    // Close drawer
    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());

    // Submenu toggles
    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const item = toggle.closest('.header__drawer-item');
        item.classList.toggle('header__drawer-item--expanded');
      });
    });

    // Close on Escape key
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.escapeHandler);
  }

  open() {
    this.drawer.classList.add('is-open');
    this.backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus trap
    const focusableElements = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus to trigger
    if (this.trigger) {
      this.trigger.focus();
    }
  }

  isOpen() {
    return this.drawer.classList.contains('is-open');
  }
}

customElements.define('mobile-drawer', MobileDrawer);
